"use client";

import { useEffect, useState } from "react";

// CookieYes is loaded by GTM (GTM-5V7X7RPH, CookieYes CMP template on Consent
// Initialization). These are the CookieYes category ids, not Consent Mode keys.
export type ConsentCategory =
  | "functional"
  | "analytics"
  | "performance"
  | "advertisement";

type CkyConsent = {
  isUserActionCompleted?: boolean;
  categories?: Partial<Record<ConsentCategory | "necessary", boolean>>;
};

declare global {
  interface Window {
    getCkyConsent?: () => CkyConsent;
  }
}

export const hasConsent = (category: ConsentCategory): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.getCkyConsent?.().categories?.[category] === true;
  } catch {
    return false;
  }
};

/**
 * True only when the visitor has made a choice in the banner and that choice
 * leaves `category` off. Unlike `!hasConsent()`, this is false while CookieYes
 * is still loading, so callers can safely delete stored state on it.
 */
export const isConsentDenied = (category: ConsentCategory): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const consent = window.getCkyConsent?.();
    if (!consent?.isUserActionCompleted) return false;
    return consent.categories?.[category] !== true;
  } catch {
    return false;
  }
};

// The docs name both banner-load events; listen for either.
const EVENTS = [
  "cookieyes_consent_update",
  "cookieyes_banner_load",
  "cookieyes_banner_loaded",
];

/** Calls `cb` on every banner load and consent change. Returns the unsubscribe. */
export const onConsentChange = (cb: () => void): (() => void) => {
  EVENTS.forEach((name) => document.addEventListener(name, cb));
  return () => EVENTS.forEach((name) => document.removeEventListener(name, cb));
};

// True only once the visitor has accepted `category` in the CookieYes banner
// (now or on an earlier visit). Flips back to false if they withdraw it.
export const useConsent = (category: ConsentCategory): boolean => {
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    const sync = () => setGranted(hasConsent(category));
    sync();
    return onConsentChange(sync);
  }, [category]);

  return granted;
};
